import { loadFallbackCategoryOrProduct, loadFallbackDefaultLocalePage } from './pageRouteFallback'
import {
  buildLocalizedPageContent,
  buildPreviewPageContent,
  fetchPageRouteResponse,
} from './pageRouteFetch'
import type { LoadedRouteContentData, PageRoute, RouteContentLoaderLocaleContext } from './types'

/**
 * Load page or preview route content.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const loadPageOrPreviewRouteContent = async ({
  route,
  signal,
  localeContext,
}: {
  route: PageRoute
  signal: AbortSignal
  localeContext: RouteContentLoaderLocaleContext
}): Promise<LoadedRouteContentData> => {
  const pageResponse = await fetchPageRouteResponse({
    route,
    signal,
    locale: localeContext.currentLocale,
  })

  if (pageResponse.ok) {
    const data = (await pageResponse.json()) as { page?: Record<string, unknown> }
    if (route.mode === 'preview') return buildPreviewPageContent({ route, data })
    return buildLocalizedPageContent({ data, signal })
  }

  if (route.mode === 'preview') {
    throw new Error(pageResponse.status === 404 ? 'Pagina non trovata' : 'Errore caricamento pagina')
  }

  if (pageResponse.status !== 404) {
    throw new Error('Errore caricamento pagina')
  }

  const fallbackPage = await loadFallbackDefaultLocalePage({
    route,
    signal,
    requestedLocale: localeContext.currentLocale,
  })
  if (fallbackPage) return fallbackPage

  return loadFallbackCategoryOrProduct({ route, signal, localeContext })
}
